<script setup lang="ts">
import { safeAnalyticsApplicationKey } from '~/utils/analyticsPrivacy'
const props = defineProps<{ open: boolean }>()
const analytics = useAnalytics()
const emit = defineEmits<{ toggle: []; close: [] }>()
const route = useRoute()
const trigger = useTemplateRef<{ focus: () => void; element?: HTMLButtonElement }>('trigger')
const grid = useTemplateRef<HTMLElement>('grid')
const popoverId = useId()
const titleId = `${popoverId}-title`
const { applications, status, error, refresh } = useApplicationLauncher()
let skipNextCloseTracking = false

function isCurrent(applicationRoute: string) {
  return applicationRoute === '/'
    ? route.path === '/'
    : route.path === applicationRoute || route.path.startsWith(`${applicationRoute}/`)
}

function close({ restoreFocus = false } = {}) {
  if (!props.open) return
  emit('close')
  if (restoreFocus) nextTick(() => trigger.value?.focus())
}

function selectApplication(application?: { id?: string }) {
  analytics.trackEvent('launcher_select', {
    source: 'header',
    target: 'same_tab',
    application_key: safeAnalyticsApplicationKey(application?.id),
  })
  analytics.setNavigationSource('application_launcher')
  skipNextCloseTracking = true
  close()
}

function moveFocus(event: KeyboardEvent) {
  if (!['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End'].includes(event.key))
    return
  const links = [
    ...(grid.value?.querySelectorAll<HTMLAnchorElement>('.application-launcher-tile') ?? []),
  ]
  const current = links.indexOf(document.activeElement as HTMLAnchorElement)
  if (current < 0 || !links.length) return
  event.preventDefault()
  const columns = matchMedia('(max-width: 520px)').matches ? 2 : 3
  const delta =
    event.key === 'ArrowLeft'
      ? -1
      : event.key === 'ArrowRight'
        ? 1
        : event.key === 'ArrowUp'
          ? -columns
          : event.key === 'ArrowDown'
            ? columns
            : 0
  const target =
    event.key === 'Home'
      ? 0
      : event.key === 'End'
        ? links.length - 1
        : Math.min(links.length - 1, Math.max(0, current + delta))
  links[target]?.focus()
}

watch(
  () => props.open,
  async (open) => {
    if (!open && skipNextCloseTracking) {
      skipNextCloseTracking = false
      return
    }
    analytics.trackEvent(open ? 'launcher_open' : 'launcher_close', { source: 'header' })
    if (!open) return
    await refresh()
    await nextTick()
    const target =
      grid.value?.querySelector<HTMLAnchorElement>('[aria-current="page"]') ??
      grid.value?.querySelector<HTMLAnchorElement>('.application-launcher-tile')
    target?.focus()
  },
)
watch(
  () => route.fullPath,
  () => close(),
)
</script>

<template>
  <div
    class="application-launcher"
    :class="{ 'is-open': open }"
  >
    <UiIconButton
      ref="trigger"
      variant="chrome"
      class="application-launcher-trigger"
      type="button"
      :aria-expanded="open"
      aria-haspopup="dialog"
      :aria-controls="popoverId"
      :aria-label="`${open ? 'Fermer' : 'Ouvrir'} le lanceur d’applications`"
      @click.stop="emit('toggle')"
    >
      <span
        class="application-launcher-glyph"
        aria-hidden="true"
      >
        <i
          v-for="index in 9"
          :key="index"
        />
      </span>
    </UiIconButton>

    <UiPopover
      :id="popoverId"
      :open="open"
      :anchor="trigger?.element"
      label="Applications Morning"
      :width="460"
      class="application-launcher-popover"
      @close="close()"
    >
      <header class="application-launcher-heading">
        <div>
          <span>Morning</span>
          <h2 :id="titleId">Applications</h2>
        </div>
        <p>Accès rapide à vos espaces</p>
      </header>

      <div
        v-if="status === 'pending'"
        class="application-launcher-state"
        aria-live="polite"
      >
        <span
          class="application-launcher-loader"
          aria-hidden="true"
        />
        Chargement des applications…
      </div>
      <div
        v-else-if="error"
        class="application-launcher-state is-error"
        role="alert"
      >
        <strong>Applications indisponibles</strong>
        <span>Le reste de Morning reste accessible.</span>
        <button
          type="button"
          @click="refresh()"
        >
          Réessayer
        </button>
      </div>
      <nav
        v-else
        aria-label="Applications Morning"
      >
        <ul
          ref="grid"
          class="application-launcher-grid"
          @keydown="moveFocus"
        >
          <li
            v-for="(application, index) in applications"
            :key="application.id"
            :style="{ '--launcher-index': index, '--launcher-app-tone': application.color }"
          >
            <NuxtLink
              :to="application.route"
              class="application-launcher-tile"
              :class="{ 'is-current': isCurrent(application.route) }"
              :aria-current="isCurrent(application.route) ? 'page' : undefined"
              @click="selectApplication(application)"
            >
              <span class="application-launcher-icon">
                <DashboardApplicationIcon
                  :application="{
                    name: application.label,
                    icon: { type: 'lucide', value: application.icon },
                  }"
                />
              </span>
              <strong>{{ application.label }}</strong>
              <i
                v-if="isCurrent(application.route)"
                aria-hidden="true"
              />
              <span
                v-if="isCurrent(application.route)"
                class="sr-only"
                >Application active</span
              >
            </NuxtLink>
          </li>
        </ul>
      </nav>
    </UiPopover>
  </div>
</template>
